// HypothesisExplorer.jsx — v3
// RR ANTIFRAGILIDADE · Hypothesis explorer with LIVE data aggregation
//
// v3 changes from v2:
//   - Causal chain: SVG flow graph with connecting arrows, colored stage nodes,
//     lag timeline bar, pulse on current-stage detection
//   - Stats strip below hero (sensor count · regime count · signal status · chain depth)
//   - Main signal chart: range selector tabs (12M / 5A / TUDO), reference line at threshold
//   - Sensor sparklines: taller (60px), with inline YoY delta badge + threshold reference line
//   - Position structure: mini sparkline per ticker when price series available
//   - Overall: more breathing room, better hierarchy, animations on section entry

import { useState, useEffect, useCallback, useMemo } from "react";
import LogoRR_MAGMA from "../assets/logos/LOGO RR MAGMA.svg";
import HypothesisDetail from "./HypothesisDetail";

// ── API roots ──────────────────────────────────────────────────────────────
// DEV: hitting Go service directly over Tailscale.
// PROD: swap to "" so it goes same-origin through nginx /api/thesis/ routing.
const THESIS_API  = "/api/thesis";
const GENOMA_UI   = "";
const MAGMA_ASSET = "https://relatorioreservado.com.br/magma/asset/stock";
const LOGO_HOST = "/api/logos";

// ── Floating ticker logo (IndexHeroBanner style) ───────────────────────────
function TickerFloat({ ticker, base, color, size = 96 }) {
  const [failed, setFailed] = useState(false);
  const monogram = (ticker || "").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: size }}>
      {!failed ? (
        <img src={`${LOGO_HOST}/logos/${base}.svg`} alt={ticker} onError={() => setFailed(true)}
          style={{ width: size, height: size, objectFit: "contain",
            filter: "drop-shadow(0 0 10px rgba(255,255,255,0.45)) drop-shadow(0 4px 10px rgba(0,0,0,0.65))",
          }} />
      ) : (
        <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic", fontSize: size * 0.38, fontWeight: 700,
          color: "rgba(255,255,255,0.88)", textShadow: "0 0 14px rgba(255,255,255,0.35), 0 3px 10px rgba(0,0,0,0.6)",
        }}>{monogram}</div>
      )}
      <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: "0.08em",
        textShadow: "0 1px 6px rgba(0,0,0,0.9)",
      }}>{ticker}</span>
    </div>
  );
}

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
  bg: "#07070a", surf: "#0d0d14", card: "#111118", border: "#1e1e2a",
  gold: "#c9a227", red: "#f03e3e", green: "#34d399", blue: "#60a5fa",
  amber: "#fbbf24", purple: "#c084fc", cyan: "#22d3ee", orange: "#fb923c",
  white: "#e8e8f0", dim: "rgba(232,232,240,0.80)", dimmer: "rgba(232,232,240,0.62)",
  faint: "rgba(232,232,240,0.35)",
};

// ── Hero images — thematic CDN images per hypothesis ──────────────────────
const HERO_IMAGES = {
  H01: "https://images.unsplash.com/photo-1550989460-0adf9ea622e2?w=1800&h=900&fit=crop&q=80",       // food/grocery aisle — caloric substitution
  H02: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1800&h=900&fit=crop&q=80",       // hospital corridor — death magnetic
  H03: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1800&h=900&fit=crop&q=80",       // solar panels — grid secession
  H06: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1800&h=900&fit=crop&q=80",       // trading screens — shadow clearing
  H07: "https://images.unsplash.com/photo-1605745341112-85968b19335b?w=1800&h=900&fit=crop&q=80",       // cargo ship — shadow fleet
  H08: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1800&h=900&fit=crop&q=80",          // brain/neuro — neuro-volatility
  H13: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1800&h=900&fit=crop&q=80",       // library/books — provocation tree
  H14: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=1800&h=900&fit=crop&q=80",       // parliament — executive impulse
  H15: "https://images.unsplash.com/photo-1491013516836-7db643ee125a?w=1800&h=900&fit=crop&q=80",       // empty cradle/nursery — reproductive strike
  H17: "https://images.unsplash.com/photo-1541872703-74c5e44368f9?w=1800&h=900&fit=crop&q=80",          // protest/democracy — democratic erosion
  H18: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=1800&h=900&fit=crop&q=80",       // wheat field — cereal paradox
  H19: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1800&h=900&fit=crop&q=80",       // legal documents — regulatory theater
  H20: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1800&h=900&fit=crop&q=80",       // hospital — saúde dual
  H21: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1800&h=900&fit=crop&q=80",       // medical — mental surge
  H22: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=1800&h=900&fit=crop&q=80",       // rio skyline — capital divergence
  H23: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=1800&h=900&fit=crop&q=80",          // newborn — perinatal win
  H24: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=1800&h=900&fit=crop&q=80",       // currency/coins — commodity currency
  H26: "https://images.unsplash.com/photo-1643841170740-d9f8ee7ed5ef?w=1800&h=900&fit=crop&q=80",       // empty playground — fertility collapse
  STATE_DEGRADATION: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1800&h=900&fit=crop&q=80", // matrix/data — meta hypothesis
};
const HERO_FALLBACK = "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1800&h=900&fit=crop&q=80"; // earth from space

function getHeroImage(detail) {
  return detail.hero_image || HERO_IMAGES[detail.id] || HERO_FALLBACK;
}

// ── Signal helpers ─────────────────────────────────────────────────────────
function signalStyle(status, signal) {
  if (status === "AGUARDANDO") return { bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.35)", color: C.amber, label: "AGUARDANDO", icon: "◌" };
  if (status === "STALE") return { bg: "rgba(120,113,108,0.14)", border: "rgba(120,113,108,0.35)", color: "#a3a3a3", label: "STALE", icon: "◆" };
  if (signal === "OVER") return { bg: "rgba(240,62,62,0.14)", border: "rgba(240,62,62,0.40)", color: C.red, label: "FIRING", icon: "●" };
  if (signal === "APPROACHING") return { bg: "rgba(251,191,36,0.14)", border: "rgba(251,191,36,0.40)", color: C.amber, label: "PRÓXIMO", icon: "▲" };
  if (signal === "UNDER") return { bg: "rgba(52,211,153,0.10)", border: "rgba(52,211,153,0.30)", color: C.green, label: "LIVE", icon: "●" };
  return { bg: "rgba(96,165,250,0.10)", border: "rgba(96,165,250,0.30)", color: C.blue, label: "LIVE", icon: "●" };
}

const REGIME_META = {
  EQUILIBRIUM: { color: C.green, dot: "●" },
  INFLATIONARY_STRESS: { color: C.orange, dot: "◆" },
  GEOPOLITICAL_ESCALATION: { color: C.red, dot: "◆" },
  STATE_DEGRADATION: { color: C.amber, dot: "▲" },
  GRID_SECESSION: { color: C.cyan, dot: "▲" },
  CREDIT_IMPLOSION: { color: C.purple, dot: "●" },
  SUPPLY_DISRUPTION: { color: C.blue, dot: "▲" },
  ALL: { color: C.purple, dot: "◈" },
};

const KILL_LEVEL = {
  primary: "MORTE PRIMÁRIA", secondary: "MORTE SECUNDÁRIA", tertiary: "MORTE TERCIÁRIA",
  "primary-long": "MORTE LONG", "primary-short": "MORTE SHORT", meta: "META-FALSIFICAÇÃO",
};

// ── Formatters ─────────────────────────────────────────────────────────────
function shortMonth(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  return `${months[parseInt(mo)-1]}/${y.slice(2)}`;
}
function fmtValue(v, decimals = 2) {
  if (v == null || isNaN(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(decimals)}`;
}
function fmtRatio(v) {
  if (v == null || isNaN(v)) return "—";
  return v.toFixed(2);
}

// ── Session rotation memory ────────────────────────────────────────────────
const SEEN_KEY = "rr_hypothesis_seen_v1";
function getSeen() {
  try { return JSON.parse(sessionStorage.getItem(SEEN_KEY)) || []; } catch { return []; }
}
function pushSeen(id) {
  const cur = getSeen();
  sessionStorage.setItem(SEEN_KEY, JSON.stringify([id, ...cur.filter(x => x !== id)].slice(0, 5)));
}

// ── API fetchers ───────────────────────────────────────────────────────────
async function fetchHypothesesList() {
  const res = await fetch(`${THESIS_API}/rr/v1/hypotheses`);
  if (!res.ok) throw new Error(`list ${res.status}`);
  return res.json();
}
async function fetchHypothesis(id) {
  const res = await fetch(`${THESIS_API}/rr/v1/hypotheses/${id}`);
  if (!res.ok) throw new Error(`detail ${res.status}`);
  return res.json();
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// EXTRACTED SUB-COMPONENTS — imported from separate files
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function HypothesisExplorer() {
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartRange, setChartRange] = useState("ALL");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const l = await fetchHypothesesList();
        if (cancelled) return;
        setList(l);
        const seen = getSeen();
        const unseen = l.filter(h => !seen.includes(h.id));
        const pool = unseen.length > 0 ? unseen : l;
        const pick = pool[Math.floor(Math.random() * pool.length)];
        if (pick) { setActiveId(pick.id); pushSeen(pick.id); }
      } catch (e) {
        if (!cancelled) setError(`lista: ${e.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setLoading(true); setError(null); setChartRange("ALL");
    (async () => {
      try {
        const d = await fetchHypothesis(activeId);
        if (!cancelled) { setDetail(d); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(`detalhe: ${e.message}`); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  const switchTo = useCallback((id) => {
    setActiveId(id);
    pushSeen(id);
  }, []);

  const reroll = useCallback(() => {
    if (list.length === 0) return;
    const seen = getSeen();
    const unseen = list.filter(h => !seen.includes(h.id) && h.id !== activeId);
    const pool = unseen.length > 0 ? unseen : list.filter(h => h.id !== activeId);
    const pick = pool[Math.floor(Math.random() * pool.length)];
    if (pick) switchTo(pick.id);
  }, [list, activeId, switchTo]);

  const isLive = detail?.status === "done";
  const main = detail?.main_signal;

  // Chart range filtering
  const filteredSeries = useMemo(() => {
    if (!main?.series) return [];
    const s = main.series;
    if (chartRange === "ALL") return s;
    if (chartRange === "12M") return s.slice(-12);
    if (chartRange === "5A") return s.slice(-60);
    return s;
  }, [main?.series, chartRange]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,600;0,700;1,400&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
        * { box-sizing: border-box; }
        .rr-hyp-tab:hover { border-color: rgba(255,255,255,0.18) !important; }
        .rr-hyp-reroll:hover { background: rgba(255,255,255,0.12) !important; }
        .rr-hyp-reroll:active { transform: scale(0.97); }
        .rr-hyp-ticker:hover { background: rgba(255,255,255,0.06) !important; transform: translateX(2px); }
        .rr-hyp-genoma-link:hover { text-decoration: underline; }
        @keyframes rr-hyp-spin { to { transform: rotate(360deg); } }
        @keyframes rr-hyp-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes rr-hyp-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
        @keyframes rr-hyp-glow { 0%, 100% { box-shadow: 0 0 8px rgba(240,62,62,0.3); } 50% { box-shadow: 0 0 20px rgba(240,62,62,0.5); } }
        .rr-hyp-fade { animation: rr-hyp-fade 0.35s ease-out; }
        .rr-hyp-firing { animation: rr-hyp-pulse 1.6s ease-in-out infinite; }
        .rr-hyp-glow { animation: rr-hyp-glow 2s ease-in-out infinite; }
        @media (max-width: 900px) {
          .rr-hyp-split { grid-template-columns: 1fr !important; }
          .rr-hyp-chain-flow { flex-direction: column !important; }
          .rr-hyp-chain-flow > div { min-width: 0 !important; }
          .rr-hyp-chain-flow svg { transform: rotate(90deg); }
          .rr-hyp-sensor-row { grid-template-columns: 1fr !important; row-gap: 10px !important; }
          .rr-hyp-stats-strip { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .rr-hyp-stats-strip { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{
        background: "linear-gradient(180deg, #0a0a0a 0%, #141414 100%)",
        border: "1px solid #1e1e1e", borderRadius: 14, overflow: "hidden",
        position: "relative", fontFamily: "'IBM Plex Mono', monospace", color: C.white,
      }}>
        {/* Grid overlay */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.4, pointerEvents: "none",
          backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
        }} />

        {/* ─── HERO + TICKER LOGOS ─────────────────────────────────────── */}
        {detail && (
          <div className="rr-hyp-fade" key={detail.id} style={{
            position: "relative", minHeight: 540, overflow: "hidden",
            background: `url(${getHeroImage(detail)}) center/cover`,
            isolation: "isolate",
          }}>
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(180deg, rgba(7,7,10,0.55) 0%, rgba(7,7,10,0.20) 25%, rgba(7,7,10,0.50) 55%, rgba(7,7,10,0.94) 100%)",
            }} />

            {/* Top badges + reroll */}
            <div style={{ position: "absolute", top: 22, left: 26, right: 26, zIndex: 4, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <img src={LogoRR_MAGMA} alt="RR Magma" style={{ width: 96, height: "auto", display: "block" }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: C.gold, background: "rgba(201,162,39,0.12)", border: "1px solid rgba(201,162,39,0.3)", padding: "4px 10px", borderRadius: 3, letterSpacing: "0.14em", backdropFilter: "blur(8px)" }}>RR ANTIFRAGILIDADE</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.red, background: "rgba(240,62,62,0.10)", border: "1px solid rgba(240,62,62,0.30)", padding: "4px 10px", borderRadius: 3, letterSpacing: "0.14em", backdropFilter: "blur(8px)" }}>✕ HIPÓTESE FALSIFICÁVEL</span>
              {isLive && <span style={{ fontSize: 11, fontWeight: 700, color: C.green, background: "rgba(52,211,153,0.14)", border: "1px solid rgba(52,211,153,0.35)", padding: "4px 10px", borderRadius: 3, letterSpacing: "0.14em", backdropFilter: "blur(8px)" }}>● SINAL LIVE</span>}
              <button className="rr-hyp-reroll" onClick={reroll} disabled={loading} style={{
                marginLeft: "auto",
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 4, color: C.white, fontSize: 11, fontWeight: 700,
                padding: "8px 14px", cursor: loading ? "wait" : "pointer",
                fontFamily: "inherit", letterSpacing: "0.12em",
                display: "flex", alignItems: "center", gap: 6,
                backdropFilter: "blur(8px)", transition: "all 0.15s",
              }}>
                <span style={{ display: "inline-block", animation: loading ? "rr-hyp-spin 1s linear infinite" : "none" }}>↻</span> TROCAR HIPÓTESE
              </button>
            </div>

            {/* Title block — top-anchored to leave room for logos */}
            <div style={{ position: "absolute", top: 80, left: 26, right: 26, zIndex: 3, maxWidth: 720 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, fontWeight: 700,
                  color: detail.color, letterSpacing: "0.14em",
                  textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                }}>{detail.id}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: C.dim,
                  letterSpacing: "0.14em", textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                }}>{(detail.category || "").toUpperCase()} · TIER {detail.tier} · {(detail.engine || "").toUpperCase()}</span>
              </div>
              <h2 style={{
                fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 700,
                color: C.white, margin: 0, lineHeight: 1.05, letterSpacing: "-0.02em",
                textShadow: "0 3px 20px rgba(0,0,0,0.75)", marginBottom: 8,
              }}>{detail.title}</h2>
              <p style={{
                fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(14px, 1.6vw, 17px)",
                color: "rgba(255,255,255,0.85)", fontStyle: "italic", margin: 0, lineHeight: 1.35,
                textShadow: "0 2px 8px rgba(0,0,0,0.7)", maxWidth: 560,
              }}>{detail.subtitle}</p>
            </div>

            {/* ── Floating ticker logos — two columns: LONG | SHORT ── */}
            {detail.positions && (detail.positions.long?.tickers?.length > 0 || detail.positions.short?.tickers?.length > 0) && (() => {
              const longReal = (detail.positions.long?.tickers || []).filter(t => !t.includes(" ") && t.length <= 10);
              const shortReal = (detail.positions.short?.tickers || []).filter(t => !t.includes(" ") && t.length <= 10);
              if (longReal.length === 0 && shortReal.length === 0) return null;
              return (
                <div style={{ position: "absolute", bottom: 24, left: 26, right: 26, zIndex: 3 }}>
                  <div style={{ height: 1, marginBottom: 18,
                    background: `linear-gradient(90deg, ${detail.color}00, ${detail.color}55, ${detail.color}00)`,
                  }} />
                  <div style={{ display: "grid", gridTemplateColumns: shortReal.length > 0 && longReal.length > 0 ? "1fr 1fr" : "1fr", gap: 24 }}>
                    {/* LONG column */}
                    {longReal.length > 0 && (
                      <div>
                        <span style={{ display: "inline-block", marginBottom: 14, fontSize: 10, fontWeight: 700, color: C.green, letterSpacing: "0.14em",
                          background: "rgba(52,211,153,0.15)", border: "1px solid rgba(52,211,153,0.40)",
                          padding: "3px 10px", borderRadius: 3, backdropFilter: "blur(6px)",
                        }}>▲ LONG</span>
                        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                          {longReal.map(t => <TickerFloat key={t} ticker={t} base={t.split(".")[0]} color={C.green} />)}
                        </div>
                      </div>
                    )}
                    {/* SHORT column */}
                    {shortReal.length > 0 && (
                      <div>
                        <span style={{ display: "inline-block", marginBottom: 14, fontSize: 10, fontWeight: 700, color: C.red, letterSpacing: "0.14em",
                          background: "rgba(240,62,62,0.15)", border: "1px solid rgba(240,62,62,0.40)",
                          padding: "3px 10px", borderRadius: 3, backdropFilter: "blur(6px)",
                        }}>▼ SHORT</span>
                        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                          {shortReal.map(t => <TickerFloat key={t} ticker={t} base={t.split(".")[0]} color={C.red} />)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ─── TABS ─────────────────────────────────────────────────────── */}
        {list.length > 0 && (
          <div style={{ padding: "12px 26px", display: "flex", gap: 8, overflowX: "auto", borderBottom: "1px solid #1e1e1e" }}>
            {list.map(h => {
              const isActive = h.id === activeId;
              return (
                <button key={h.id} onClick={() => { setActiveId(h.id); pushSeen(h.id); }} className="rr-hyp-tab" style={{
                  flexShrink: 0, padding: "10px 16px", borderRadius: 6,
                  border: isActive ? `1px solid ${h.color}50` : "1px solid #1e1e1e",
                  background: isActive ? `${h.color}12` : "transparent",
                  color: isActive ? h.color : "rgba(255,255,255,0.55)",
                  fontSize: 13, fontWeight: isActive ? 700 : 500,
                  whiteSpace: "nowrap", fontFamily: "inherit",
                  cursor: "pointer", letterSpacing: "0.03em", transition: "all 0.15s",
                }}>
                  <span style={{ fontWeight: 700 }}>{h.id}</span>
                  <span style={{ marginLeft: 10, opacity: 0.85 }}>{h.title}</span>
                  {h.status === "teaser" && <span style={{ marginLeft: 10, fontSize: 9, letterSpacing: "0.12em", color: C.amber, opacity: 0.75 }}>· TEASER</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* ─── LOADING / ERROR ──────────────────────────────────────────── */}
        {loading && (
          <div style={{ padding: "60px 26px", textAlign: "center", color: C.dimmer, fontSize: 13, letterSpacing: "0.06em" }}>
            <span style={{ display: "inline-block", animation: "rr-hyp-spin 1s linear infinite" }}>↻</span> &nbsp; Agregando sensores...
          </div>
        )}
        {error && !loading && (
          <div style={{ padding: "20px 26px", background: "rgba(240,62,62,0.05)", border: "1px solid rgba(240,62,62,0.2)", color: C.red, fontSize: 13, margin: 26, borderRadius: 6 }}>Erro: {error}</div>
        )}

        {/* ─── DETAIL ───────────────────────────────────────────────────── */}
        {detail && !loading && !error && (
          <HypothesisDetail
            detail={detail}
            chartRange={chartRange}
            setChartRange={setChartRange}
            list={list}
            switchTo={switchTo}
          />
        )}
      </div>
    </>
  );
}